<script>
  import { t } from '../i18n/index.js';
  import { themeId, inwdDetails } from '../stores/theme.js';
  import { getTheme } from '../themes/index.js';

  /**
   * The page-level loading indicator: shown while a whole page waits for its
   * data, never for a button, a draft save or a panel inside a page (those
   * keep `Spinner`).
   *
   * A CSS ring by default. When the active theme offers a forest loader
   * (registry capability `details.loader === 'forest'`) and the person has
   * turned its details on, the illustrated `<inwd-forest-loader>` takes its
   * place; the module and its artwork are only fetched then, in a chunk of
   * their own. If that fetch fails the ring stays, so the page still says it
   * is loading. Reduced motion is honoured on both: the forest stays inked
   * with a slow pulse (the element's own branch), the ring stands still.
   *
   * `essential` forces the ring — used while the app has not yet learnt the
   * account's preferences.
   */
  export let label = '';
  export let essential = false;

  $: text = label || $t('app.loading');
  $: wantsForest = !essential && $inwdDetails && getTheme($themeId).details?.loader === 'forest';

  let forestReady = false;
  let forestFailed = false;
  let requested = false;

  $: if (wantsForest && !requested) {
    requested = true;
    import('../vendor/inwd-loader/inwd-forest-loader.js')
      .then(() => { forestReady = true; })
      .catch((error) => {
        console.warn('[page-loader] forest loader unavailable, using the ring', error);
        forestFailed = true;
      });
  }

  $: showForest = wantsForest && forestReady && !forestFailed;
</script>

<div class="page-loader">
  {#if showForest}
    <inwd-forest-loader class="page-loader-forest" label={text} duration="1.4"></inwd-forest-loader>
    <!-- The forest is hidden by the theme's own tokens when it does not apply
         (dark mode keeps it, Essenziale never reaches here); the ring is the
         fallback the stylesheet shows instead. -->
    <div class="page-loader-ring" role="status"><span class="sr-only">{text}</span></div>
  {:else}
    <div class="page-loader-ring page-loader-ring-forced" role="status"><span class="sr-only">{text}</span></div>
  {/if}
</div>
